import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { Form as FormikForm, Formik } from 'formik';
import React from 'react';
import Yup from 'yup';
import type { FormField, OnSubmit } from '../../design-system';
import { FormEditable, FormReadonly, Section } from '../../design-system';

interface WizardStepContextProps<S extends Record<string, unknown>> {
  fields: FormField[];
  initialValues: S;
  locked?: boolean;
  name: string;
  onCancel: OnSubmit<S>;
  onSubmit: OnSubmit<S>;
  step: number;
  subtitle: string;
  validationSchema: Yup.ObjectSchema;
}

export function WizardStep<S extends Record<string, unknown>>(
  props: React.PropsWithChildren<WizardStepContextProps<S>>,
): React.ReactElement {
  const { children, fields, initialValues, locked, name, onCancel, onSubmit, step, subtitle, validationSchema } = props;

  return (
    <Section title={name} description={subtitle}>
      <Formik<S> initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit} validateOnChange>
        {(formikProps) => {
          const { errors, isSubmitting, touched, values } = formikProps;
          return (
            <FormikForm>
              {locked ? (
                <FormReadonly fields={fields} initialValues={initialValues} />
              ) : (
                <FormEditable fields={fields} errors={errors} touched={touched} disabled={isSubmitting} />
              )}
              {children}
              <Box pt={2} display="flex" flexDirection="row-reverse">
                <Button color="primary" disabled={isSubmitting} type="submit" variant="contained">
                  {isSubmitting ? <CircularProgress size={24} color="inherit" /> : 'Next'}
                </Button>
                <Box mr="auto" />
                <Button color="inherit" onClick={() => onCancel(values, formikProps)} variant="contained">
                  {step === 0 ? 'Cancel' : 'Back'}
                </Button>
              </Box>
            </FormikForm>
          );
        }}
      </Formik>
    </Section>
  );
}
